import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  areEqualIds,
  buildInviteCalendarOptions,
  buildInviteTaskBoardOptions,
  getInviteCodeFromSearch,
  parseInviteMeta,
  sanitizeSelection,
  TeamCalendarOptionSource,
  TeamInvitePreviewMember,
  TeamShareOption,
  TeamTaskBoardOptionSource,
} from '../components/Team/utils';
import { trackEventMixpanel } from '../utils';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const TEAM_INVITE_QUERY_KEYS = ['inviteCode', 'invite', 'code'];

interface TeamInviteAcceptRequest {
  inviteCode: string;
  sharedCalendarIds: string[];
  sharedTaskBoardIds: string[];
  shareEnergy: boolean;
}

interface UseTeamInviteFlowParams {
  teamId?: string;
  inviteCodeParam?: string;
  locationSearch: string;
  activeTeamId?: string;
  calendarsMeta: TeamCalendarOptionSource[];
  todoProjects: TeamTaskBoardOptionSource[];
  isCalendarsLoading: boolean;
  isTaskBoardsLoading: boolean;
  availableCalendarIdsLookup: Record<string, boolean>;
  availableTaskBoardIdsLookup: Record<string, boolean>;
  createTeamInvite: (
    teamId: string,
    inviteEmail: string,
    role?: 'admin' | 'member'
  ) => Promise<string | null>;
  isCreatingTeamInvite: boolean;
  createTeamInviteError: string | null;
  clearCreateTeamInviteError: () => void;
  resolveTeamInvite: (inviteCode: string) => Promise<unknown | null>;
  clearTeamInviteResolveError: () => void;
  acceptTeamInvite: (teamInviteAcceptReq: TeamInviteAcceptRequest) => Promise<string | null>;
  clearAcceptTeamInviteError: () => void;
  isAcceptingTeamInvite: boolean;
  onInviteAccepted: (acceptedTeamId: string) => void;
  onInviteDeclined: () => void;
}

export const useTeamInviteFlow = ({
  teamId,
  inviteCodeParam,
  locationSearch,
  activeTeamId,
  calendarsMeta,
  todoProjects,
  isCalendarsLoading,
  isTaskBoardsLoading,
  availableCalendarIdsLookup,
  availableTaskBoardIdsLookup,
  createTeamInvite,
  isCreatingTeamInvite,
  createTeamInviteError,
  clearCreateTeamInviteError,
  resolveTeamInvite,
  clearTeamInviteResolveError,
  acceptTeamInvite,
  clearAcceptTeamInviteError,
  isAcceptingTeamInvite,
  onInviteAccepted,
  onInviteDeclined,
}: UseTeamInviteFlowParams) => {
  const { t } = useTranslation();
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteValidationError, setInviteValidationError] = useState<string | null>(null);
  const [generatedInviteUrl, setGeneratedInviteUrl] = useState<string | null>(null);
  const [isCreateTeamInviteModalOpen, setIsCreateTeamInviteModalOpen] = useState(false);
  const [isCopyingInviteLink, setIsCopyingInviteLink] = useState(false);
  const [isInviteLinkCopied, setIsInviteLinkCopied] = useState(false);
  const [resolvedInviteTeamName, setResolvedInviteTeamName] = useState('');
  const [resolvedInviteRole, setResolvedInviteRole] = useState<'admin' | 'member'>('member');
  const [resolvedInviteInviterName, setResolvedInviteInviterName] = useState('');
  const [resolvedInvitePreviewMembers, setResolvedInvitePreviewMembers] = useState<
    TeamInvitePreviewMember[]
  >([]);
  const [resolvedInviteMemberSummaryText, setResolvedInviteMemberSummaryText] = useState('');
  const [selectedInviteCalendarIds, setSelectedInviteCalendarIds] = useState<string[]>([]);
  const [selectedInviteTaskBoardIds, setSelectedInviteTaskBoardIds] = useState<string[]>([]);
  const [isInviteShareEnergyEnabled, setIsInviteShareEnergyEnabled] = useState(true);
  const [isInviteSelectionInitialized, setIsInviteSelectionInitialized] = useState(false);
  const [isViewerEligibleForInvite, setIsViewerEligibleForInvite] = useState(true);
  const [inviteBlockedReason, setInviteBlockedReason] = useState('');

  const inviteCodeFromQuery = useMemo(() => {
    return getInviteCodeFromSearch(locationSearch, TEAM_INVITE_QUERY_KEYS);
  }, [locationSearch]);
  const activeInviteCode = inviteCodeParam || inviteCodeFromQuery;
  const isAcceptInviteModalOpen = Boolean(activeInviteCode);
  const inviteTeamName = resolvedInviteTeamName;

  const inviteCalendarOptions = useMemo<TeamShareOption[]>(() => {
    return buildInviteCalendarOptions(calendarsMeta, t);
  }, [calendarsMeta, t]);
  const inviteTaskBoardOptions = useMemo<TeamShareOption[]>(() => {
    return buildInviteTaskBoardOptions(todoProjects, t);
  }, [t, todoProjects]);
  const defaultInviteCalendarIds = useMemo(() => {
    return inviteCalendarOptions.map((calendar) => calendar.id);
  }, [inviteCalendarOptions]);
  const defaultInviteTaskBoardIds = useMemo(() => {
    return inviteTaskBoardOptions.map((taskBoard) => taskBoard.id);
  }, [inviteTaskBoardOptions]);
  const normalizedInviteCalendarIds = useMemo(() => {
    return sanitizeSelection(
      selectedInviteCalendarIds,
      availableCalendarIdsLookup,
      isCalendarsLoading
    );
  }, [selectedInviteCalendarIds, availableCalendarIdsLookup, isCalendarsLoading]);
  const normalizedInviteTaskBoardIds = useMemo(() => {
    return sanitizeSelection(
      selectedInviteTaskBoardIds,
      availableTaskBoardIdsLookup,
      isTaskBoardsLoading
    );
  }, [selectedInviteTaskBoardIds, availableTaskBoardIdsLookup, isTaskBoardsLoading]);

  const normalizedInviteEmail = inviteEmail.trim().toLowerCase();
  const isInviteEmailValid = EMAIL_REGEX.test(normalizedInviteEmail);
  const isGenerateInviteDisabled =
    isCreatingTeamInvite || normalizedInviteEmail.length === 0 || !isInviteEmailValid;
  const createTeamInviteErrorMessage = inviteValidationError || createTeamInviteError;

  useEffect(() => {
    setInviteEmail('');
    setInviteValidationError(null);
    setGeneratedInviteUrl(null);
    setIsCreateTeamInviteModalOpen(false);
    setIsCopyingInviteLink(false);
    setIsInviteLinkCopied(false);
    setResolvedInviteTeamName('');
    setResolvedInviteRole('member');
    setResolvedInviteInviterName('');
    setResolvedInvitePreviewMembers([]);
    setResolvedInviteMemberSummaryText('');
    setSelectedInviteCalendarIds([]);
    setSelectedInviteTaskBoardIds([]);
    setIsInviteShareEnergyEnabled(true);
    setIsInviteSelectionInitialized(false);
    setIsViewerEligibleForInvite(true);
    setInviteBlockedReason('');
    clearCreateTeamInviteError();
    clearTeamInviteResolveError();
    clearAcceptTeamInviteError();
  }, [teamId, clearCreateTeamInviteError, clearTeamInviteResolveError, clearAcceptTeamInviteError]);

  useEffect(() => {
    if (!isInviteLinkCopied) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setIsInviteLinkCopied(false);
    }, 3000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isInviteLinkCopied]);

  useEffect(() => {
    if (!activeInviteCode) {
      setResolvedInviteTeamName('');
      setResolvedInviteRole('member');
      setResolvedInviteInviterName('');
      setResolvedInvitePreviewMembers([]);
      setResolvedInviteMemberSummaryText('');
      setSelectedInviteCalendarIds([]);
      setSelectedInviteTaskBoardIds([]);
      setIsInviteShareEnergyEnabled(true);
      setIsInviteSelectionInitialized(false);
      setIsViewerEligibleForInvite(true);
      setInviteBlockedReason('');
      clearTeamInviteResolveError();
      clearAcceptTeamInviteError();
      return;
    }

    let isActive = true;
    clearTeamInviteResolveError();
    clearAcceptTeamInviteError();
    setResolvedInviteTeamName('');
    setResolvedInviteRole('member');
    setResolvedInviteInviterName('');
    setResolvedInvitePreviewMembers([]);
    setResolvedInviteMemberSummaryText('');
    setSelectedInviteCalendarIds([]);
    setSelectedInviteTaskBoardIds([]);
    setIsInviteShareEnergyEnabled(true);
    setIsInviteSelectionInitialized(false);
    setIsViewerEligibleForInvite(true);
    setInviteBlockedReason('');

    void (async () => {
      const resolvedInvite = await resolveTeamInvite(activeInviteCode);
      if (!isActive || !resolvedInvite) {
        return;
      }

      const parsedInviteMeta = parseInviteMeta(resolvedInvite, t);
      setResolvedInviteTeamName(parsedInviteMeta.teamName || '');
      setResolvedInviteRole(parsedInviteMeta.inviteRole);
      setResolvedInviteInviterName(parsedInviteMeta.inviterName);
      setResolvedInvitePreviewMembers(parsedInviteMeta.previewMembers);
      setResolvedInviteMemberSummaryText(parsedInviteMeta.memberSummaryText);
      setIsViewerEligibleForInvite(parsedInviteMeta.isViewerEligible);
      setInviteBlockedReason(parsedInviteMeta.viewerReason);
    })();

    return () => {
      isActive = false;
    };
  }, [
    activeInviteCode,
    clearAcceptTeamInviteError,
    clearTeamInviteResolveError,
    resolveTeamInvite,
    t,
  ]);

  useEffect(() => {
    if (
      !isAcceptInviteModalOpen ||
      !activeInviteCode ||
      isInviteSelectionInitialized ||
      !isViewerEligibleForInvite
    ) {
      return;
    }

    if (isCalendarsLoading || isTaskBoardsLoading) {
      return;
    }

    setSelectedInviteCalendarIds(defaultInviteCalendarIds);
    setSelectedInviteTaskBoardIds(defaultInviteTaskBoardIds);
    setIsInviteShareEnergyEnabled(true);
    setIsInviteSelectionInitialized(true);
  }, [
    activeInviteCode,
    defaultInviteCalendarIds,
    defaultInviteTaskBoardIds,
    isAcceptInviteModalOpen,
    isCalendarsLoading,
    isInviteSelectionInitialized,
    isViewerEligibleForInvite,
    isTaskBoardsLoading,
  ]);

  useEffect(() => {
    if (!areEqualIds(selectedInviteCalendarIds, normalizedInviteCalendarIds)) {
      setSelectedInviteCalendarIds(normalizedInviteCalendarIds);
    }
  }, [normalizedInviteCalendarIds, selectedInviteCalendarIds]);

  useEffect(() => {
    if (!areEqualIds(selectedInviteTaskBoardIds, normalizedInviteTaskBoardIds)) {
      setSelectedInviteTaskBoardIds(normalizedInviteTaskBoardIds);
    }
  }, [normalizedInviteTaskBoardIds, selectedInviteTaskBoardIds]);

  const openCreateTeamInviteModal = () => {
    trackEventMixpanel('team_invite_modal_opened', {
      team_id: activeTeamId,
      source: 'team_invite_flow',
    });
    setInviteEmail('');
    setInviteValidationError(null);
    setGeneratedInviteUrl(null);
    setIsCopyingInviteLink(false);
    setIsInviteLinkCopied(false);
    clearCreateTeamInviteError();
    setIsCreateTeamInviteModalOpen(true);
  };

  const closeCreateTeamInviteModal = () => {
    if (isCreatingTeamInvite) {
      return;
    }

    setIsCreateTeamInviteModalOpen(false);
    setInviteEmail('');
    setInviteValidationError(null);
    setGeneratedInviteUrl(null);
    setIsCopyingInviteLink(false);
    setIsInviteLinkCopied(false);
    clearCreateTeamInviteError();
  };

  const handleInviteEmailChange = (nextEmail: string) => {
    setInviteEmail(nextEmail);
    setGeneratedInviteUrl(null);
    setIsInviteLinkCopied(false);

    if (inviteValidationError) {
      setInviteValidationError(null);
    }

    if (createTeamInviteError) {
      clearCreateTeamInviteError();
    }
  };

  const handleGenerateInviteLink = async () => {
    if (!normalizedInviteEmail) {
      setInviteValidationError(t('team.invite.validation.emailRequired'));
      return;
    }

    if (!EMAIL_REGEX.test(normalizedInviteEmail)) {
      setInviteValidationError(t('team.invite.validation.emailInvalid'));
      return;
    }

    if (!activeTeamId) {
      setInviteValidationError(t('team.invite.validation.teamNotFound'));
      return;
    }

    setInviteValidationError(null);
    clearCreateTeamInviteError();

    const inviteUrl = await createTeamInvite(activeTeamId, normalizedInviteEmail, 'member');
    if (!inviteUrl) {
      return;
    }

    trackEventMixpanel('team_invite_link_generated', {
      team_id: activeTeamId,
    });
    setGeneratedInviteUrl(inviteUrl);
    setIsInviteLinkCopied(false);
  };

  const handleCopyInviteLink = async () => {
    if (!generatedInviteUrl || isCopyingInviteLink) {
      return;
    }

    setIsCopyingInviteLink(true);

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(generatedInviteUrl);
      }

      setIsInviteLinkCopied(true);
      trackEventMixpanel('team_invite_link_copied', {
        team_id: activeTeamId,
      });
    } finally {
      setIsCopyingInviteLink(false);
    }
  };

  const handleToggleInviteCalendar = (calendarId: string) => {
    const nextCalendarIds = selectedInviteCalendarIds.includes(calendarId)
      ? selectedInviteCalendarIds.filter((id) => id !== calendarId)
      : [...selectedInviteCalendarIds, calendarId];

    setSelectedInviteCalendarIds(
      sanitizeSelection(nextCalendarIds, availableCalendarIdsLookup, isCalendarsLoading)
    );
  };

  const handleToggleInviteTaskBoard = (taskBoardId: string) => {
    const nextTaskBoardIds = selectedInviteTaskBoardIds.includes(taskBoardId)
      ? selectedInviteTaskBoardIds.filter((id) => id !== taskBoardId)
      : [...selectedInviteTaskBoardIds, taskBoardId];

    setSelectedInviteTaskBoardIds(
      sanitizeSelection(nextTaskBoardIds, availableTaskBoardIdsLookup, isTaskBoardsLoading)
    );
  };

  const handleToggleInviteEnergy = () => {
    setIsInviteShareEnergyEnabled((prevValue) => !prevValue);
  };

  const handleDeclineInvite = () => {
    if (isAcceptingTeamInvite) {
      return;
    }

    clearTeamInviteResolveError();
    clearAcceptTeamInviteError();
    trackEventMixpanel('team_invite_declined', {
      has_invite_code: Boolean(activeInviteCode),
    });
    setResolvedInviteTeamName('');
    setResolvedInviteRole('member');
    setResolvedInviteInviterName('');
    setResolvedInvitePreviewMembers([]);
    setResolvedInviteMemberSummaryText('');
    setSelectedInviteCalendarIds([]);
    setSelectedInviteTaskBoardIds([]);
    setIsInviteShareEnergyEnabled(true);
    setIsInviteSelectionInitialized(false);
    setIsViewerEligibleForInvite(true);
    setInviteBlockedReason('');
    onInviteDeclined();
  };

  const handleAcceptInvite = async () => {
    if (!activeInviteCode || !isViewerEligibleForInvite) {
      return;
    }

    clearAcceptTeamInviteError();
    const acceptedTeamId = await acceptTeamInvite({
      inviteCode: activeInviteCode,
      sharedCalendarIds: normalizedInviteCalendarIds,
      sharedTaskBoardIds: normalizedInviteTaskBoardIds,
      shareEnergy: isInviteShareEnergyEnabled,
    });

    if (!acceptedTeamId) {
      return;
    }

    trackEventMixpanel('team_invite_accepted', {
      team_id: acceptedTeamId,
      shared_calendar_count: normalizedInviteCalendarIds.length,
      shared_task_board_count: normalizedInviteTaskBoardIds.length,
      share_energy: isInviteShareEnergyEnabled,
    });
    onInviteAccepted(acceptedTeamId);
  };

  return {
    activeInviteCode,
    isAcceptInviteModalOpen,
    inviteTeamName,
    resolvedInviteRole,
    resolvedInviteInviterName,
    resolvedInvitePreviewMembers,
    resolvedInviteMemberSummaryText,
    isViewerEligibleForInvite,
    inviteBlockedReason,
    inviteCalendarOptions,
    inviteTaskBoardOptions,
    normalizedInviteCalendarIds,
    normalizedInviteTaskBoardIds,
    isInviteShareEnergyEnabled,
    inviteEmail,
    createTeamInviteErrorMessage,
    generatedInviteUrl,
    isCreateTeamInviteModalOpen,
    isCopyingInviteLink,
    isInviteLinkCopied,
    isGenerateInviteDisabled,
    openCreateTeamInviteModal,
    closeCreateTeamInviteModal,
    handleInviteEmailChange,
    handleGenerateInviteLink,
    handleCopyInviteLink,
    handleToggleInviteCalendar,
    handleToggleInviteTaskBoard,
    handleToggleInviteEnergy,
    handleAcceptInvite,
    handleDeclineInvite,
  };
};
